/**
 * The anchor host in an AR placement session (TraceBackend#999).
 *
 * With placements, an AR session mounts the scene frame at the selected source’s
 * `T_world←L · P⁻¹` and the anchor host (the document root the content hangs
 * below, in the marker frame) stays at `P_primary`. Content therefore renders
 * at `T_world←selected × inverse(P_selected) × P_primary × C`. The two
 * placement poses cancel only when the selected marker is the primary.
 *
 * The host's pose therefore comes from `P`, never from the transform the
 * opened file carries: an editor can save its desktop-preview pose (`Rx(θ)`
 * and a Y lift) into the document root record, and the legacy XR path, which
 * uses that record as-is, composes it a second time.
 */

import type { TraceScene } from "../core/TraceScene";
import type { PlacementPose } from "../cms/localizers";
import { anchorHost, applyPlacementAnchorPose } from "../loaders/ImageMarkerLoader";
import { remountEditorRoots } from "../editor/authoring/documentRoot";

/**
 * Pose the anchor host at `pose` for the session and return a function that
 * puts the host back exactly as it was (call it when the session ends). An
 * editor root under the host stays in the scene frame throughout.
 */
export function poseAnchorHostForSession(traceScene: TraceScene, pose: PlacementPose): () => void {
    const host = anchorHost(traceScene);
    if (!host) return () => {};
    const position = host.transform.position.clone();
    const rotation = host.transform.rotationQuaternion?.clone() ?? null;
    const euler = host.transform.rotation.clone();
    applyPlacementAnchorPose(traceScene, pose);
    return () => {
        if (host.transform.isDisposed()) return;
        host.transform.position.copyFrom(position);
        host.transform.rotation.copyFrom(euler);
        host.transform.rotationQuaternion = rotation;
        remountEditorRoots(host);
    };
}
